import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PurchaseAppService from '../../../../application/platform/scp/purchase/purchase-app-service';
import BaseAppService from '../../../../application/platform/scp/base/base-app-service';
import EntityAppService from '../../../../application/platform/scp/entity/entitiy-app-service';
import { ACTION_TYPES, usePurchaseCreateEditDispatch, usePurchaseCreateEditState } from './purchase-create-edit-context';
import { useFeedback } from '../../../../hooks/use-feedback';
import { IPurchaseAddEditRequest } from '../../../../model/scp/purchase/request/purchase-add-edit-request';
import { IPurchaseResponse } from '../../../../model/scp/purchase/response/purchase-response';
import { useBaseData } from '../../../../application/context/base-data-context';
import { sanitizeText } from '../../../../utils/text';
import { hasErrors } from '../../../../utils/validation';
import { validatePurchase } from './purchase-validation';
import { countDraftItems } from '../../shared/scp-utils';

const LIST_ROUTE = '/compras-licitacoes';
const PURCHASE_DOCUMENT_TYPE = 1;

const FIELD_BY_INPUT: Record<string, keyof IPurchaseAddEditRequest> = {
    'select-legal-support': 'legalSupportId',
    'select-type-calling-instrument': 'typeCallingInstrumentId',
    'select-modality': 'modalityId',
    'select-type-document': 'typeDocumentId',
    'select-dispute-mode': 'disputeModeId',
    'select-cod-buyer-unit': 'codBuyerUnit',
    'input-year-purchase': 'yearPurchase',
    'input-number': 'numberPurchase',
    'input-process-number': 'processNumber',
    'input-object': 'objectPurchase',
    'input-title': 'title',
    'input-information-complementary': 'informationComplementary',
    'select-client': 'clientId',
};

const SANITIZED_INPUTS = new Set(['input-object', 'input-title', 'input-information-complementary']);

function toFormRequest(data: IPurchaseResponse): IPurchaseAddEditRequest {
    return {
        id: data.id,
        title: data.title,
        situationId: data.situationId,
        informationComplementary: data.informationComplementary || '',
        clientId: data.clientId,
        codBuyerUnit: data.unidadeOrgao?.unitCode?.toString() || '',
        typeCallingInstrumentId: data.typeCallingInstrumentId,
        modalityId: data.modalityId,
        disputeModeId: data.disputeModeId,
        numberPurchase: data.numberPurchase || '',
        yearPurchase: data.yearPurchase,
        processNumber: data.processNumber,
        objectPurchase: data.objectPurchase || '',
        notes: data.notes,
        indSrp: data.indSrp,
        proposalOpeningDate: data.proposalOpeningDate,
        proposalCloseDate: data.proposalCloseDate,
        legalSupportId: data.legalSupportId,
        urlSystemOrigin: data.urlSystemOrigin,
    };
}

export const usePurchaseCreateEditController = () => {
    const navigate = useNavigate();
    const { showError, showSuccess, withLoading } = useFeedback();
    const [isSaving, setIsSaving] = useState(false);
    const dispatch = usePurchaseCreateEditDispatch();
    const state = usePurchaseCreateEditState();
    const baseData = useBaseData();
    const isEdit = state.purchaseId !== undefined;

    useEffect(() => {
        loadInitialData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!baseData.loaded) return;
        dispatch({ type: ACTION_TYPES.setInstrumentSummons, payload: baseData.instrumentSummons });
        dispatch({ type: ACTION_TYPES.setModality, payload: baseData.modality });
        dispatch({ type: ACTION_TYPES.setDisputeMode, payload: baseData.disputeMode });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [baseData.loaded]);

    async function loadInitialData() {
        try {
            await withLoading(async () => {
                const [legalSupport, typeDocument, unidEntity] = await Promise.all([
                    BaseAppService.listLegalSupport(),
                    BaseAppService.listTypeDocument(PURCHASE_DOCUMENT_TYPE),
                    EntityAppService.listUnidEntity(),
                ]);
                dispatch({ type: ACTION_TYPES.setLegalSupport, payload: legalSupport });
                dispatch({ type: ACTION_TYPES.setTypeDocument, payload: typeDocument });
                dispatch({ type: ACTION_TYPES.setUnidEntity, payload: unidEntity });

                if (state.purchaseId === undefined) return;
                const data = await PurchaseAppService.getPurchase(state.purchaseId);
                // O amparo legal depende do instrumento e da modalidade: recarrega antes de preencher o formulário
                if (data.typeCallingInstrumentId || data.modalityId) {
                    const filteredLegalSupport = await BaseAppService.listLegalSupport(data.typeCallingInstrumentId, data.modalityId);
                    dispatch({ type: ACTION_TYPES.setLegalSupport, payload: filteredLegalSupport });
                }
                dispatch({ type: ACTION_TYPES.setPurchaseRequest, payload: toFormRequest(data) });
            });
        } catch (error: any) {
            showError('Não foi possível carregar os dados da compra/licitação', error);
        }
    }

    function handleTabChange(_event: any, newTabIndex: number) {
        dispatch({ type: ACTION_TYPES.setTabIndex1, payload: newTabIndex });
    }

    function handleOnChange(e: any) {
        const { name, value } = e.target;
        const field = FIELD_BY_INPUT[name];
        if (!field) return;
        dispatch({ type: ACTION_TYPES.setPurchaseRequest, payload: { [field]: SANITIZED_INPUTS.has(name) ? sanitizeText(value) : value } });

        if (name === 'select-type-calling-instrument') reloadLegalSupport(value, state.purchaseRequest.modalityId);
        if (name === 'select-modality') reloadLegalSupport(state.purchaseRequest.typeCallingInstrumentId, value);
    }

    async function reloadLegalSupport(instrumentSummonsId?: number | null, modalityId?: number | null) {
        try {
            const legalSupport = await BaseAppService.listLegalSupport(instrumentSummonsId ?? undefined, modalityId ?? undefined);
            dispatch({ type: ACTION_TYPES.setLegalSupport, payload: legalSupport });
            // A seleção anterior pode não existir na nova lista
            dispatch({ type: ACTION_TYPES.setPurchaseRequest, payload: { legalSupportId: 0 } });
        } catch (error: any) {
            showError('Não foi possível recarregar o amparo legal', error);
        }
    }

    function handleDateChange(field: keyof IPurchaseAddEditRequest, value: string | null) {
        dispatch({ type: ACTION_TYPES.setPurchaseRequest, payload: { [field]: value } });
    }

    function setFileUpload(file: File) {
        dispatch({ type: ACTION_TYPES.setFile, payload: file });
    }

    function setItemsPurchaseJson(json: string) {
        dispatch({ type: ACTION_TYPES.setItemsPurchaseJson, payload: json });
    }

    function navigateToList() {
        navigate(LIST_ROUTE);
    }

    async function persist() {
        if (state.purchaseId !== undefined) {
            await PurchaseAppService.updatePurchase(state.purchaseId, state.purchaseRequest);
            showSuccess('Compra/licitação atualizada com sucesso!');
            navigateToList();
            return;
        }
        const created = await PurchaseAppService.addPurchase(state.purchaseRequest, state.file!, state.itemsPurchaseJson);
        showSuccess('Compra/licitação adicionada com sucesso!');
        // Abre a edição do registro criado para lançar resultados e anexar arquivos
        navigate(created?.id ? `${LIST_ROUTE}/publicar/${created.id}` : LIST_ROUTE, { replace: true });
    }

    async function addOrEditPurchase() {
        if (isSaving) return;
        const errors = validatePurchase(state.purchaseRequest, { isEdit, hasFile: !!state.file });
        if (hasErrors(errors)) {
            dispatch({ type: ACTION_TYPES.setShowValidationErrors, payload: true });
            showError('Preencha todos os campos obrigatórios');
            return;
        }
        if (!isEdit && countDraftItems(state.itemsPurchaseJson) === 0) {
            showError('É necessário adicionar pelo menos um item à compra/licitação');
            return;
        }

        setIsSaving(true);
        try {
            await withLoading(persist);
        } catch (error: any) {
            showError('Não foi possível salvar a compra/licitação', error);
        } finally {
            setIsSaving(false);
        }
    }

    return {
        isEdit,
        isSaving,
        handleTabChange,
        handleOnChange,
        handleDateChange,
        setFileUpload,
        setItemsPurchaseJson,
        addOrEditPurchase,
        navigateToList,
    };
};
